import { FC, PropsWithChildren, useEffect } from 'react'
import { Navigate, useParams } from 'react-router'
import { useLocalStorage, useReadLocalStorage } from 'usehooks-ts'

import { LocalStorageKey, removeLocalStorageValue } from '@/common/utils/localStorage'
import LoadAnimation from '@/components/loadAnimation'
import { monitorBreadcrumb, monitorMessage } from '@/modules/monitoring'
import { useRegion, useRegions } from '@/modules/urlRouting/hooks'
import { NoAccessTemplate } from '@/modules/urlRouting/NoAccessTemplate'
import { NoProductAccessPage } from '@/modules/urlRouting/NoProductAccessPage'
import { getRegionStorageKey } from '@/modules/urlRouting/regionStorage'
import { generatePathWithRegionId } from '@/modules/urlRouting/utils'
import { useCurrentPermissions } from '@/modules/user/hooks'

import { useGetRegionLandingPage } from '../permissions/pageAccessHooks'

/**
 * Redirect to region with default home page when missing in path. For example:
 * - /oakland/maps (valid region id with Access) => do nothing
 * - /berkeley (valid region id but no access) => redirect to /:defaultRegionId
 * - /blah (invalid region id) = redirect to /:defaultRegionId
 *
 */
const RegionGatekeeper: FC<PropsWithChildren> = ({ children }) => {
  const { regionId } = useParams()
  const { data: region, isPending } = useRegion(regionId)

  // No :regionId in the path is not a denial - it is the root route asking to be
  // sent to a default region. useRegion is disabled without one, which in
  // TanStack Query v5 leaves isPending true and data undefined forever, so this
  // has to be checked before both guards below.
  if (!regionId) return <NavigateToDefaultRegion />

  if (isPending) return <LoadAnimation />

  if (!region) {
    monitorBreadcrumb(
      'RegionGatekeeper: No Access to Region (or region does not exist), Pushing to Default Region',
      { regionId }
    )
    return <NavigateToDefaultRegion />
  }

  return <RegionLocalStorageSync>{children}</RegionLocalStorageSync>
}

const RegionLocalStorageSync: FC<PropsWithChildren> = ({ children }) => {
  const { regionId } = useParams()
  const { key, hasIdentity } = getRegionStorageKey()
  const [storedRegionId, setStoredRegionId] = useLocalStorage<string>(key, '')

  useEffect(() => {
    // Without an identity there is nobody to remember this for, and writing it would put
    // it somewhere the next account could read.
    if (!hasIdentity) return
    if (regionId && storedRegionId != regionId) setStoredRegionId(regionId)
  }, [hasIdentity, regionId, storedRegionId, setStoredRegionId])

  useEffect(() => {
    // Before the region was scoped per identity it lived under one browser-wide key.
    // Nothing reads that key now, so drop whatever a previous account left in it.
    removeLocalStorageValue(LocalStorageKey.REGION_ID)
  }, [])

  return children
}

/**
 * Provided a regionId, redirects to a default "Home Page" for a user:
 *  - if user has mobility manager => Live Map
 *  - else if user has Parking Insights => PI Revenue
 *  - else if user has curb Regulations => Regulations Map
 *  - else if user has Digital Smart Zones => Activity Map
 */
export const NavigateToRegionLandingPage: FC = () => {
  const regionLandingPage = useGetRegionLandingPage()
  const { regionId } = useParams()
  // isPending, not isLoading: the permissions query is disabled while auth
  // loads, and a disabled query reports isLoading false with data undefined -
  // which every permission hook reads as "no access".
  const { isPending, isError, error } = useCurrentPermissions()

  if (isPending) return <LoadAnimation />

  // A failed permissions fetch looks exactly like "no products" to the hooks
  // above, and the client does not retry. Hand it to the error boundary rather
  // than telling the user their org is misconfigured because a request blipped.
  if (isError) throw error

  if (!regionLandingPage) return <NoProductAccessPage />

  monitorBreadcrumb(`Navigation: Push to Default page for Region: ${regionId}`)

  return <Navigate replace to={generatePathWithRegionId(regionLandingPage, regionId)} />
}

export const NavigateToDefaultRegion: FC = () => {
  const { regionId } = useParams()
  const { key } = getRegionStorageKey()
  const rememberedRegionId = useReadLocalStorage<string>(key)
  // Never look up the id already in the path. Doing so re-observes the very
  // query the gatekeeper above just failed on, and re-observing an errored
  // query refetches it - which, for a query that never held data, resets its
  // status to pending. The gatekeeper then sees a pending region again and
  // unmounts this component before it can navigate, so the two livelock and
  // the page spins forever. It could never be the destination anyway.
  const candidateRegionId =
    rememberedRegionId && rememberedRegionId !== regionId ? rememberedRegionId : undefined
  // isLoading, not isPending: with no candidate this query is disabled, and it
  // has to read as "not loading" so we fall through to the regions list below
  // instead of waiting on a request that will never happen.
  const { data: rememberedRegion, isLoading: isLoadingRegion } = useRegion(candidateRegionId)
  const { data: regions, isLoading: isLoadingRegions } = useRegions()

  const rememberedDestination = rememberedRegion?.regionId
  // Skipping the region we came from is what stops a bounce back into the route
  // that sent us here; taking the first of the rest keeps every other region on
  // every page as a candidate.
  const destination =
    rememberedDestination ??
    regions?.pages.flatMap(page => page.items).find(region => region.regionId !== regionId)
      ?.regionId
  const source = rememberedDestination ? 'remembered region' : 'first accessible region'

  useEffect(() => {
    if (isLoadingRegion || isLoadingRegions || !destination) return
    monitorBreadcrumb('Navigation: Push to Default Region', {
      urlRegionId: regionId,
      destination,
      source,
    })
  }, [destination, isLoadingRegion, isLoadingRegions, regionId, source])

  if (isLoadingRegion || isLoadingRegions) return <LoadAnimation />

  if (destination) return <Navigate replace to={`/${destination}`} />

  return <NoRegionAvailablePage />
}

const NoRegionAvailablePage: FC = () => {
  const { regionId } = useParams()
  const { key } = getRegionStorageKey()
  const rememberedRegionId = useReadLocalStorage<string>(key)

  useEffect(() => {
    monitorMessage(
      'User has no accessible regions: GET /regions returned an empty list. ' +
        'Likely an org misconfiguration, e.g. organizations.regions = {ALL} instead of NULL ' +
        '(the backend intersects the org region list with real region ids, and "ALL" matches none).',
      { urlRegionId: regionId, rememberedRegionId }
    )
  }, [regionId, rememberedRegionId])

  return <NoAccessTemplate primaryText="You do not have access to any Regions" />
}

export default RegionGatekeeper
